import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGetFresh } from '../lib/api';
import { getApiBase } from '../lib/submissions';
import { readTtlCache, writeTtlCache } from '../lib/localTtlCache';
import { RAISED_FUNDS_SUMMARY_DEFAULT_POLL_MS } from '../config/polling';

export type RaisedFundsFund = {
  key: string;
  label: string;
  type: 'tiltify' | 'primary-match' | 'secondary-match' | 'power-hour-match' | 'bank' | string;
  cap?: number | null;
  baseAmount?: number | null;
  matchAmount?: number | null;
  matchRate?: number | null;
  bucketTotal: number;
  displayAmount?: number | null;
  remainingCap?: number | null;
  percentToCap?: number | null;
  secondaryBase?: number | null;
  currency?: string | null;
  status?: 'active' | 'ended' | null;
  capReached?: boolean;
};

export type RaisedFundsSummary = {
  tiltify: {
    amountRaised: number;
    currency: string;
    error?: string;
  };
  bankAmount: number;
  funds: RaisedFundsFund[];
  totals: {
    matchRate: number;
    totalPrimaryMatching: number;
    totalSecondaryMatching: number;
    totalPowerHourMatching?: number;
    totalMatching: number;
    secondaryBase: number;
    grandTotal: number;
  };
  settings?: {
    subhead?: string;
    headline?: string;
    tickerIntervalMs?: number;
    directDonationLabel?: string;
    gridBorderColor?: string;
    gridHeader?: string;
  };
  debugMode: boolean;
  debugAmount?: number;
  fetchedAt: string;
};

type Options = {
  pollMs?: number;
};

const API_BASE = getApiBase().replace(/\/+$/, '');
const SUMMARY_ENDPOINT = `${API_BASE}/raised-funds/v1/summary`;

// Instant-first-paint cache for returning visitors (preserves the P4A-86/P4A-122
// anti-flash fix). TTL is a safety margin above the poll interval, not a
// freshness target — real staleness is judged against the cache's own
// timestamp (initialDataUpdatedAt below), not against mount time.
const LS_KEY = 'p4a_raised_funds_cache';
const LS_TTL_MS = 30_000;

const fetchSummary = async (): Promise<RaisedFundsSummary> => {
  const res = await apiGetFresh(SUMMARY_ENDPOINT);
  if (!res.ok) {
    throw new Error(`Failed to load raised funds summary (${res.status})`);
  }
  const json = await res.json();
  writeTtlCache(LS_KEY, json);
  return json;
};

export default function useRaisedFundsSummary(options: Options = {}) {
  const pollMs = typeof options.pollMs === 'number' ? options.pollMs : RAISED_FUNDS_SUMMARY_DEFAULT_POLL_MS;
  const cached = useMemo(() => readTtlCache<RaisedFundsSummary>(LS_KEY, LS_TTL_MS), []);
  const query = useQuery<RaisedFundsSummary, Error>({
    queryKey: ['raised-funds-summary'],
    queryFn: fetchSummary,
    refetchInterval: pollMs,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    staleTime: pollMs,
    initialData: cached?.data,
    // Seed the real cache timestamp (not "now") so React Query judges the seeded
    // data's staleness correctly and only fires the default mount refetch when
    // it's actually older than pollMs — avoids an extra fetch on every navigation
    // when another still-mounted consumer (e.g. PowerHourGlow) already has fresh data.
    initialDataUpdatedAt: cached?.cachedAt,
  });

  return {
    data: query.data,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
